import React from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  Rocket, 
  Award, 
  HeartHandshake,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Container, Section, Badge, Button } from '../ui';
import { useIntersectionObserver } from '../../hooks';

const reasons = [
  {
    icon: Users,
    title: 'Expert Technical Talent',
    description: 'Access top-tier developers, architects, and data scientists with proven expertise in modern technologies.',
    highlights: ['150+ Tech Experts', 'Vetted Professionals', 'Contract & Full-Time'],
  },
  {
    icon: Rocket,
    title: 'Accelerated Delivery',
    description: 'Agile methodologies and efficient processes ensure rapid development without compromising quality.',
    highlights: ['Sprint-Based Delivery', 'CI/CD Pipelines', 'DevOps Excellence'],
  },
  {
    icon: Award,
    title: 'Proven Track Record',
    description: 'Over a decade of successful project deliveries across diverse industries and technologies.',
    highlights: ['200+ Projects', '10+ Years', '98% Satisfaction'],
  },
  {
    icon: HeartHandshake,
    title: 'Partnership Approach',
    description: "We're not just vendors — we're strategic partners invested in your long-term success.",
    highlights: ['Dedicated Teams', 'Transparent Communication', 'Long-Term Relationships'],
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0 },
};

export const WhyChooseUs: React.FC = () => {
  const { elementRef, isVisible } = useIntersectionObserver({ threshold: 0.1 });

  return (
    <Section background="white" padding="lg" id="why-choose-us">
      <Container>
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div>
            <Badge variant="accent" className="mb-4">Why ISTATEC</Badge>
            <h2 className="heading-lg text-navy-900 mb-6">
              Your Trusted Technology Partner
            </h2>
            <p className="text-body text-lg mb-8">
              With a decade of experience and a team of 150+ experts, we deliver 
              technology solutions that drive real business outcomes. Our commitment 
              to excellence and partnership approach sets us apart.
            </p>

            {/* Reasons Grid */}
            <motion.div
              ref={elementRef}
              className="space-y-6"
              variants={containerVariants}
              initial="hidden"
              animate={isVisible ? 'visible' : 'hidden'}
            >
              {reasons.map((reason) => (
                <motion.div
                  key={reason.title}
                  variants={itemVariants}
                  className="group"
                >
                  <div className="flex gap-4">
                    {/* Icon */}
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-navy-100 to-accent-50 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <reason.icon className="w-6 h-6 text-navy-700" />
                    </div>

                    {/* Content */}
                    <div>
                      <h3 className="text-lg font-semibold text-navy-900 mb-1">
                        {reason.title}
                      </h3>
                      <p className="text-navy-600 text-sm mb-3">
                        {reason.description}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {reason.highlights.map((highlight) => (
                          <span
                            key={highlight}
                            className="inline-flex items-center gap-1 text-xs font-medium text-accent-600 bg-accent-50 px-2 py-1 rounded-full"
                          >
                            <CheckCircle2 className="w-3 h-3" />
                            {highlight}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* CTA */}
            <div className="mt-10">
              <Link to="/about">
                <Button variant="primary" size="lg" rightIcon={<ArrowRight className="w-5 h-5" />}>
                  Learn More About Us
                </Button>
              </Link>
            </div>
          </div>

          {/* Right Content - Visual */}
          <motion.div
            className="relative hidden lg:block"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isVisible ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            {/* Main Visual Container */}
            <div className="relative">
              {/* Background Decoration */}
              <div className="absolute inset-0 bg-gradient-to-br from-navy-100 to-accent-100 rounded-3xl transform rotate-3" />
              
              {/* Main Card */}
              <div className="relative bg-white rounded-3xl shadow-xl p-8 transform -rotate-1 hover:rotate-0 transition-transform duration-500">
                {/* Testimonial Preview */}
                <div className="p-6 bg-navy-900 rounded-2xl text-white">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-accent-400 to-accent-600 flex items-center justify-center text-lg font-bold">
                      JD
                    </div>
                    <div>
                      <p className="text-sm text-navy-200 italic mb-2">
                        "ISTATEC transformed our development velocity. Their team 
                        integrated seamlessly and delivered beyond expectations."
                      </p>
                      <div className="text-sm font-semibold">John Doe</div>
                      <div className="text-xs text-navy-400">CTO, TechCorp Inc.</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Elements */}
              <motion.div
                className="absolute -top-6 -right-6 w-20 h-20 bg-accent-500 rounded-2xl flex items-center justify-center shadow-lg"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
              >
                <Award className="w-10 h-10 text-white" />
              </motion.div>
              
              <motion.div
                className="absolute -bottom-4 -left-4 w-16 h-16 bg-navy-900 rounded-xl flex items-center justify-center shadow-lg"
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 5, repeat: Infinity }}
              >
                <Users className="w-8 h-8 text-white" />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </Container>
    </Section>
  );
};

